import React, { useEffect, useState, useRef } from "react";
import { 
  GetDataFromApiWithParams
} from "../../../../../services/api/ExecuteApiRequests";
import { 
  Box, 
  Button, 
  IconButton, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  TextField,
  Typography,
  Chip,
  CircularProgress,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  Container,
  Divider
} from "@mui/material";
import { 
  Search as SearchIcon,
  Visibility as ViewIcon,
  Print as PrintIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon
} from "@mui/icons-material";
import { useReactToPrint } from "react-to-print";
import Barcode from "react-barcode";
import QRCode from "qrcode.react"; 
import { IPDCToastMessageResult } from "../../../../../components/Controls";
import useDecodedUser from "../../../../../services/hooks/useDecodedUser";
import { QRCodeCanvas } from "qrcode.react"; // Correct named import

export default function AdvancePaymentList() {
  // ==================== STATE DECLARATIONS ====================
  const [paymentData, setPaymentData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  
  // Selected record for view dialog
  const [selectedRow, setSelectedRow] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  
  // Toast Notification
  const [toastMessage, setToastMessage] = useState({ message: "", type: "" });
  
  // Ref for printable receipt content
  const receiptContentRef = useRef();
  
  // ==================== HOOKS ====================
  const decodedUser = useDecodedUser();
  
  // ==================== TOAST FUNCTIONS ====================
  const showToast = (message, type = "success") => {
    setToastMessage({ message, type });
  };

  const showSuccessToast = (message) => {
    showToast(message, "success");
  };

  const showErrorToast = (message) => {
    showToast(message, "error");
  };

  const showWarningToast = (message) => {
    showToast(message, "warning");
  };

  // ==================== PRINT FUNCTION ====================
  // ==================== PRINT FUNCTION ====================
// ==================== PRINT FUNCTION ====================
// ==================== PRINT FUNCTION ====================
// ==================== PRINT FUNCTION ====================
// ==================== PRINT FUNCTION ====================
const handlePrint = useReactToPrint({
  content: () => receiptContentRef.current,
  documentTitle: `Advance_Payment_Receipt_${selectedRow?.id || 'New'}`,
  pageStyle: `
    @page {
      size: auto;
      margin: 45mm 0.5mm 10mm 1mm; /* top 45mm, right 0.5mm, bottom 15mm, left 0.5mm */
    }
    @media print {
      html, body {
        width: 100%;
        margin: 0;
        padding: 0;
        background: white;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      * {
        box-sizing: border-box;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      
      /* Increase font sizes for all text elements */
      body, .MuiTypography-root {
        font-size: 14pt !important;
      }
      
      /* Header styles */
      .receipt-header h1, .MuiTypography-h5 {
        font-size: 24pt !important;
        font-weight: bold !important;
      }
      
      /* Section titles */
      .MuiTypography-subtitle1 {
        font-size: 16pt !important;
        font-weight: bold !important;
        margin-bottom: 12pt !important;
      }
      
      /* Labels */
      .MuiTypography-body2, .MuiTypography-caption {
        font-size: 12pt !important;
      }
      
      /* Values */
      .MuiTypography-body2, .MuiTypography-body1 {
        font-size: 12pt !important;
      }
      
      /* Amount section */
      .amount-label {
        font-size: 12pt !important;
      }
      .amount-value {
        font-size: 18pt !important;
        font-weight: bold !important;
      }
      .amount-words {
        font-size: 12pt !important;
        font-style: italic !important;
      }
      
      /* Footer text */
      .footer-text {
        font-size: 10pt !important;
      }
      
      /* Bold text */
      strong, .MuiTypography-fontWeightMedium, .MuiTypography-fontWeightBold {
        font-weight: bold !important;
      }
      
      .MuiBox-root, .MuiGrid-root, .MuiTypography-root, .MuiPaper-root {
        color-adjust: exact;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      
      /* Ensure container uses full width with minimal margins */
      .MuiContainer-root {
        padding-left: 0 !important;
        padding-right: 0 !important;
        max-width: 100% !important;
      }
      
      .MuiGrid-container {
        display: flex !important;
        flex-wrap: wrap !important;
        width: 100% !important;
        margin: 0 !important;
      }
      .MuiGrid-item {
        margin: 0 !important;
        padding: 4px !important;
      }
      .MuiGrid-grid-xs-12 {
        flex: 0 0 100% !important;
        max-width: 100% !important;
        width: 100% !important;
      }
      .MuiGrid-grid-md-6 {
        flex: 0 0 50% !important;
        max-width: 50% !important;
        width: 50% !important;
      }
      
      /* Two column layout */
      .receipt-two-column {
        display: flex !important;
        flex-direction: row !important;
        gap: 12px !important;
        margin-bottom: 20px !important;
        width: 100% !important;
      }
      .receipt-two-column > div {
        flex: 1 !important;
        min-width: 0 !important; /* Prevents overflow */
      }
      
      /* Ensure content stays within margins */
      .receipt-content {
        max-width: 100% !important;
        padding: 0 !important;
      }
      
      /* Official stamp alignment */
      .official-stamp {
        margin-right: 0 !important;
      }
      
      /* Reduce any default padding */
      .MuiDialogContent-root {
        padding: 0 !important;
      }
      
      .MuiBox-root {
        padding-left: 0 !important;
        padding-right: 0 !important;
      }
    }
  `,
  onAfterPrint: () => showSuccessToast("Receipt printed successfully"),
  onPrintError: (error) => {
    console.error("Print error:", error);
    showErrorToast("Failed to print receipt");
  }
});

  // ==================== UTILITY FUNCTIONS ====================
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return "Invalid Date";
    }
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "$0.00";
    return `$${parseFloat(amount).toFixed(2)}`;
  };

  const numberToWords = (num) => {
    if (!num && num !== 0) return 'Zero';
    
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
      'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    
    const numToWords = (n) => {
      if (n < 20) return ones[n];
      if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
      if (n < 1000) return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + numToWords(n % 100) : '');
      if (n < 1000000) return numToWords(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + numToWords(n % 1000) : '');
      if (n < 1000000000) return numToWords(Math.floor(n / 1000000)) + ' Million' + (n % 1000000 ? ' ' + numToWords(n % 1000000) : '');
      return numToWords(Math.floor(n / 1000000000)) + ' Billion' + (n % 1000000000 ? ' ' + numToWords(n % 1000000000) : '');
    };
    
    const dollars = Math.floor(num);
    const cents = Math.round((num - dollars) * 100);
    
    let result = numToWords(dollars) + ' Dollar' + (dollars !== 1 ? 's' : '');
    if (cents > 0) {
      result += ' and ' + numToWords(cents) + ' Cent' + (cents !== 1 ? 's' : '');
    }
    
    return result;
  };

  // ==================== FETCH FUNCTIONS ====================
  const fetchAdvancePayments = async () => {
    setLoading(true);
    try {
      const response = await GetDataFromApiWithParams("finances/piadpaymentsearch");
      console.log("API Response:", response);
      
      if (response && Array.isArray(response)) {
        const formattedData = response.map((item) => ({
          id: item.id,
          agreement_id: item.agreement_id,
          payment_date: item.payment_date,
          payment_amount: item.payment_amount,
          payment_mode: item.payment_mode,
          reference_no: item.reference_no,
          remarks: item.remarks,
          status: item.status || "pending",
          requested_by: item.requested_by,
          requested_date: item.requested_date,
          payment_date_formatted: formatDate(item.payment_date),
          requested_date_formatted: formatDate(item.requested_date),
          payment_amount_formatted: formatCurrency(item.payment_amount),
        }));
        
        setPaymentData(formattedData);
        setFilteredData(formattedData);
        setTotalRecords(formattedData.length);
      } else {
        setPaymentData([]);
        setFilteredData([]);
        setTotalRecords(0);
      }
    } catch (error) {
      console.error("Error fetching advance payment data:", error);
      showErrorToast("Failed to load advance payment records");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdvancePayments();
    console.log("USER:", decodedUser);
  }, []);

  // ==================== SEARCH FUNCTIONS ====================
  const handleSearch = () => {
    try {
      setIsSearching(true);
      
      if (!searchQuery.trim()) {
        setFilteredData(paymentData);
        setTotalRecords(paymentData.length);
        setIsSearching(false);
        return;
      }
      
      const searchValue = searchQuery.toLowerCase().trim();
      const filtered = paymentData.filter(item => 
        (item.agreement_id && item.agreement_id.toString().toLowerCase().includes(searchValue)) ||
        (item.reference_no && item.reference_no.toLowerCase().includes(searchValue)) ||
        (item.remarks && item.remarks.toLowerCase().includes(searchValue)) ||
        (item.payment_mode && item.payment_mode.toLowerCase().includes(searchValue))
      );
      
      setFilteredData(filtered);
      setTotalRecords(filtered.length);
      setPage(0);
      
      if (filtered.length > 0) {
        showSuccessToast(`Found ${filtered.length} records`);
      } else {
        showWarningToast("No records found for your search");
      }
    } catch (error) {
      console.error("Error searching:", error);
      showErrorToast("Search failed! Please try again.");
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    setFilteredData(paymentData);
    setTotalRecords(paymentData.length);
    setPage(0);
    showWarningToast("Search cleared");
  };

  // ==================== VIEW FUNCTIONS ====================
  const handleViewRecord = (record) => {
    setSelectedRow(record);
    setViewDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedRow(null);
  };

  // ==================== PAGINATION FUNCTIONS ====================
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // ==================== REFRESH FUNCTIONS ====================
  const handleRefresh = () => {
    fetchAdvancePayments();
    setSearchQuery("");
    showWarningToast("Refreshing advance payment records...");
  };

  // ==================== PAGINATED DATA ====================
  const paginatedData = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // ==================== RENDER ====================
  return (
    <Container maxWidth="xl">
      {/* Toast Notification */}
      {toastMessage.message && (
        <IPDCToastMessageResult 
          message={toastMessage.message} 
          type={toastMessage.type} 
        />
      )}

      {/* Main Title */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          Paid Advance Payments
        </Typography>
        <Typography variant="body2" color="text.secondary">
          View paid advance payment records and print receipts
        </Typography>
      </Box>
          
      {/* Search Section */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              label="Search by Agreement ID, Reference No, or Remarks"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />,
              }}
              variant="outlined"
              size="small"
              disabled={loading}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSearch}
              disabled={isSearching || loading}
              startIcon={<SearchIcon />}
              sx={{
                bgcolor: '#9e9e9e',
                '&:hover': { bgcolor: '#757575' },
              }}
            >
              {isSearching ? 'Searching...' : 'Search'}
            </Button>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleRefresh}
              startIcon={<RefreshIcon />}
              disabled={loading}
              sx={{
                borderColor: '#1976d2',
                color: '#1976d2',
                '&:hover': {
                  borderColor: '#115293',
                  backgroundColor: 'rgba(25, 118, 210, 0.04)',
                },
              }}
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
        
        <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          {searchQuery && (
            <Button
              size="small"
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
              variant="outlined"
            >
              Clear Search
            </Button>
          )}
          <Chip 
            label={`Total: ${totalRecords} records`} 
            variant="outlined"
            size="small"
          />
        </Box>
      </Paper>

      {/* Results Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Agreement ID</TableCell>
                <TableCell>Payment Date</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Payment Mode</TableCell>
                <TableCell>Reference No</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={24} />
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Loading advance payment records...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="textSecondary">
                      {searchQuery 
                        ? 'No records found matching your search.' 
                        : 'No advance payment records found.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((record) => (
                  <TableRow key={record.id} hover>
                    <TableCell>{record.id}</TableCell>
                    <TableCell>{record.agreement_id}</TableCell>
                    <TableCell>{record.payment_date_formatted}</TableCell>
                    <TableCell>{record.payment_amount_formatted}</TableCell>
                    <TableCell>{record.payment_mode || 'N/A'}</TableCell>
                    <TableCell>{record.reference_no || 'N/A'}</TableCell>
                    <TableCell>
                      PAID
                    </TableCell>
                    <TableCell align="center">
                      <IconButton 
                        size="small" 
                        color="primary"
                        onClick={() => handleViewRecord(record)}
                        title="View Details & Print Receipt"
                      >
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalRecords}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* View Dialog with Print Button */}
<Dialog 
  open={viewDialogOpen} 
  onClose={handleCloseViewDialog}
  maxWidth="lg"
  fullWidth
  PaperProps={{
    sx: {
      maxWidth: '1000px',
      borderRadius: 2,
      bgcolor: '#ffffff'
    }
  }}
>
  <DialogTitle sx={{ 
    bgcolor: '#ffffff',
    borderBottom: '2px solid #1976d2', 
    py: 2 
  }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box
                       component="img"
                       src="/images/ipdc logo.jpg"
                       alt="IPDC Logo"
                       sx={{
                         height: '50px',
                         width: 'auto',
                         objectFit: 'contain'
                       }}
                     />
        <Box>
          <Typography variant="body1" fontWeight="bold" color="#000">
            IPDC REVENUE RECEIPT
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Advance Payment Receipt
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
       Paid
        <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            size="small"
            disabled={!selectedRow}
            sx={{ 
                ml: 2,
                bgcolor: '#9e9e9e', // Gray color
                '&:hover': {
                bgcolor: '#757575' // Darker gray on hover
                },
                '&:disabled': {
                bgcolor: '#e0e0e0' // Light gray when disabled
                }
            }}
            >
            Print Receipt
            </Button>
      </Box>
    </Box>
  </DialogTitle>
  <DialogContent dividers sx={{ p: 4, bgcolor: '#ffffff',  fontWeight: 'bold'  }}>
    {/* Printable Receipt Content */}
    <div ref={receiptContentRef}>
      {selectedRow && (
        <>
          {/* Print styles to ensure dialog appearance is preserved */}
          <style type="text/css">{`
            @media print {
              body { 
                margin: 0; 
                padding: 20px; 
                background: white;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              .MuiGrid-root {
                display: flex !important;
                flex-wrap: wrap !important;
                box-sizing: border-box !important;
              }
              .MuiGrid-item {
                margin: 0 !important;
                box-sizing: border-box !important;
              }
              .MuiGrid-grid-xs-12 {
                flex-grow: 0 !important;
                max-width: 100% !important;
                flex-basis: 100% !important;
                width: 100% !important;
              }
              .MuiGrid-grid-md-6 {
                flex-grow: 0 !important;
                max-width: 50% !important;
                flex-basis: 50% !important;
                width: 50% !important;
              }
              .receipt-two-column {
                display: flex !important;
                flex-direction: row !important;
                width: 100% !important;
              }
              .receipt-two-column > div {
                width: 50% !important;
                padding: 16px !important;
              }
              .no-break {
                page-break-inside: avoid !important;
              }
              .MuiTypography-root {
                color-adjust: exact !important;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
            }
          `}</style>

          {/* Receipt Header */}
<Box sx={{ 
  display: 'flex',
  alignItems: 'center',
  gap: 3,
  mb: 4, 
  pb: 2, 
  borderBottom: '1px dashed #ccc'
}}>
  {/* Logo on the left */}
  <Box
    component="img"
    src="/images/ipdc logo.jpg"
    alt="IPDC Logo"
    sx={{
      height: '150px',
      width: 'auto',
      objectFit: 'contain',
      backgroundColor: '#f0f0f0',
      display: 'block'
    }}
    onLoad={(e) => {
      console.log('Image loaded successfully!', {
        naturalWidth: e.target.naturalWidth,
        naturalHeight: e.target.naturalHeight,
        computedStyle: window.getComputedStyle(e.target)
      });
    }}
    onError={(e) => {
      console.log('Image failed to load');
    }}
  />
  
  {/* Centered text content */}
  <Box sx={{ flex: 1, textAlign: 'center' }}>
    <Typography variant="body1" fontWeight="bold" color="#000" gutterBottom>
      Industrial Projects Development Corporation (IPDC)
    </Typography>
    <Typography variant="body2" color="text.secondary">
      Advance Payment Collection Receipt
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
      Receipt No:  <Typography variant="caption" sx={{ mt: 1, color: '#000', fontWeight: 'medium' }}>
          {`Adv-${selectedRow.id?.toString().padStart(6, '0')}`}
        </Typography>
    </Typography>
    <Typography variant="body2" color="text.secondary">
      Date: {new Date().toLocaleDateString()}
    </Typography>
  </Box>
  
  {/* Barcode on the right */}
  <Box sx={{ 
    width: '150px',
    height: '150px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    border: '1px solid #e0e0e0',
    borderRadius: 1,
    p: 1
  }}>
    {selectedRow && (
      <>
        <Barcode
          value={`Adv${selectedRow.id?.toString().padStart(6, '0')}`}
          width={1.5}
          height={60}
          format="CODE128"
          displayValue={false}
          background="#ffffff"
          lineColor="#000000"
        />
        <Typography variant="caption" sx={{ mt: 1, color: '#000', fontWeight: 'medium' }}>
          {`Adv-${selectedRow.id?.toString().padStart(6, '0')}`}
        </Typography>
      </>
    )}
  </Box>
</Box>
          {/* Two Column Layout - Explicitly styled to maintain columns in print */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' },
            gap: 3,
            mb: 3,
            '@media print': {
              display: 'flex !important',
              flexDirection: 'row !important',
              gap: '24px !important'
            }
          }}>
            {/* Left Column - Agreement Details */}
            <Box sx={{ 
              flex: 1,
              '@media print': {
                flex: '1 !important',
                width: '50% !important'
              }
            }}>
              <Typography variant="body2" color="#000" gutterBottom>
                Agreement Details
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ width: '120px' }}>Agreement ID:</Typography>
                  <Typography variant="body2" fontWeight="medium" sx={{ width: '120px', color: '#000'}}>{selectedRow.agreement_id}</Typography>
                </Box>
                <Box sx={{ display: 'flex' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ width: '120px' }}>Requested By:</Typography>
                  <Typography variant="body2" sx={{ width: '120px', color: '#000'}}>{selectedRow.requested_by || 'System'}</Typography>
                </Box>
                <Box sx={{ display: 'flex' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ width: '120px' }}>Request Date:</Typography>
                  <Typography variant="body2" sx={{ width: '120px', color: '#000'}}>{selectedRow.requested_date_formatted}</Typography>
                </Box>
                <Box sx={{ display: 'flex' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ width: '120px' }}>Reference No:</Typography>
                  <Typography variant="body2" sx={{ width: '120px', color: '#000'}}>{selectedRow.reference_no || 'N/A'}</Typography>
                </Box>
              </Box>
            </Box>

            {/* Right Column - Payment Details */}
            <Box sx={{ 
              flex: 1,
              '@media print': {
                flex: '1 !important',
                width: '50% !important'
              }
            }}>
              <Typography variant="body1" color="#000" gutterBottom>
                Payment Details
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ width: '120px' }}>Payment Date:</Typography>
                  <Typography variant="body2" sx={{ width: '120px', color: '#000'}}>{selectedRow.payment_date_formatted}</Typography>
                </Box>
                <Box sx={{ display: 'flex' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ width: '120px' }}>Payment Mode:</Typography>
                  <Typography variant="body2" sx={{ width: '120px', color: '#000'}}>{selectedRow.payment_mode || 'N/A'}</Typography>
                </Box>
                <Box sx={{ display: 'flex' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ width: '120px' }}>Transaction ID:</Typography>
                  <Typography variant="body2" sx={{ width: '120px', color: '#000'}}>TXN-{selectedRow.id?.toString().padStart(8, '0')}</Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Amount Section */}
          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    Payment Amount
                  </Typography>
                  <Typography variant="body2" color="#000" sx={{ fontSize: '1rem' }}>
                    {selectedRow.payment_amount_formatted}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    Amount in Words
                  </Typography>
                  <Typography variant="caption" fontStyle="italic" sx={{ color: 'text.secondary', fontSize: '0.75rem', display: 'block' }}>
                   [ {selectedRow.payment_amount ? numberToWords(parseFloat(selectedRow.payment_amount)) : 'Zero Dollars'} Only]
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Remarks */}
          {selectedRow.remarks && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" color="#1976d2" gutterBottom>
                Remarks
              </Typography>
              <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                <Typography variant="body2">{selectedRow.remarks}</Typography>
              </Paper>
            </Box>
          )}

          
         {/* Footer */}
<Box sx={{ mt: 3, pt: 3, borderTop: '2px solid #000' }}>
  <Box sx={{ 
    display: 'flex', 
    justifyContent: 'space-between', 
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 2
  }}>
    {/* Left side - Prepared By, Approved By, Date */}
    <Box sx={{ 
      display: 'flex', 
      gap: 10, // Gap between the three items
      flexWrap: 'wrap',
      flex: 1
    }}>
      <Box>
        <Typography variant="caption" color="text.secondary">Prepared By</Typography>
        <Typography variant="body2" fontWeight="medium" sx={{ width: '120px', color: '#000'}}>
          {decodedUser?.full_name || 'System'}
        </Typography>
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary">Approved By</Typography>
        <Typography variant="body2" fontWeight="medium">
          {selectedRow.requested_by || '________________'}
        </Typography>
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary">Date</Typography>
        <Typography variant="body2" fontWeight="medium" sx={{ width: '120px', color: '#000'}}>
          {new Date().toLocaleDateString()}
        </Typography>
      </Box>
    </Box>

    {/* Right side - Official Receipt Stamp */}
    <Paper 
      elevation={0} 
      sx={{ 
        p: 2, 
        border: '2px dashed #000', 
        borderRadius: 2, 
        width: '220px',
        textAlign: 'center',
        bgcolor: '#ffffff'
      }}
    >
      <Typography variant="body2" color="text.secondary" fontWeight="bold">
        OFFICIAL RECEIPT
      </Typography>
      <Typography variant="caption" color="text.secondary">
        IPDC Finance Department
      </Typography>
    </Paper>
  </Box>
</Box>
     
          {/* Official Stamp */}
          {/* <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0 }}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 2, 
                border: '2px dashed #1976d2', 
                borderRadius: 2, 
                width: '220px',
                textAlign: 'center',
                bgcolor: '#ffffff'
              }}
            >
              <Typography variant="body2" color="#1976d2" fontWeight="bold">
                OFFICIAL RECEIPT
              </Typography>
              <Typography variant="caption" color="text.secondary">
                IPDC Finance Department
              </Typography>
            </Paper>
          </Box> */}
        </>
      )}
    </div>
  </DialogContent>
  <DialogActions sx={{ p: 2, bgcolor: '#ffffff', borderTop: '1px solid #e0e0e0' }}>
    <Button onClick={handleCloseViewDialog} variant="outlined">
      Close
    </Button>
  </DialogActions>
</Dialog>
    </Container>
  );
}